/**
 * The admin panel's handle on `@modulus/api`.
 *
 * Two deliberate differences from the dashboard:
 *
 * - **`sessionStorage`, not `localStorage`.** Closing the tab ends the session.
 *   This panel can create schools, grant licences and grant the platform role, and
 *   a token for it should not sit on disk on a laptop in a café.
 * - **A 404 from `/admin/*` means "not a platform admin".** The API answers 404
 *   rather than 403 so the surface is invisible rather than merely forbidden; the
 *   session context below relies on that and must not report it as an outage.
 */

import {
  ApiClient,
  ApiError,
  SessionExpiredError,
  createApi,
  sessionStorageSession,
  type RequestOptions,
  type StoredSession,
} from '@modulus/api'

const store = sessionStorageSession()

export const api = createApi({ store })
export const client: ApiClient = api.client

export { ApiError, SessionExpiredError }
export type { StoredSession }

export const loadSession = (): StoredSession | null => store.load()
export const clearSession = (): void => store.clear()

export function setSessionLostHandler(handler: (() => void) | null): void {
  client.onSessionLost(handler)
}

/** The catalogue store and the pages both call this. Path is relative to `/api/v1`. */
export function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return client.request<T>(path, options)
}
